/**
 * Usage-tracking READ endpoints — request schemas + wire types.
 *
 * The summary and history reads, served by `apps/api/src/routes/platform/usage.ts`
 * over the two summary RPCs of migration
 * `20260921070000_usage_tracking_foundation.sql` and the history RPC of
 * `20260923064527_usage_org_billing_history.sql` (docs/USAGE_TRACKING.md):
 *
 *   GET /api/v1/usage/orgs/:orgId/summary?month=YYYY-MM
 *       → `get_organization_usage_summary` (SECURITY INVOKER, under the
 *         caller's own org-admin RLS) — `{ rows: OrganizationUsageSummaryRow[] }`
 *   GET /api/v1/usage/orgs/:orgId/history?to=YYYY-MM
 *       → `get_organization_usage_history` (SECURITY INVOKER, the org summary
 *         walked month by month — twelve months ending at `to`) — `{ rows:
 *         OrganizationUsageHistoryRow[] }`, billed `net_amount` per
 *         (month, currency)
 *   GET /api/v1/usage/platform/summary?month=YYYY-MM
 *       → `get_platform_usage_summary` (SECURITY DEFINER, super admins)
 *         — `{ summary: PlatformUsageSummary }`
 *
 * The month rides the wire as `YYYY-MM`; the handler passes `p_month` /
 * `p_end_month` = month + '-01' because every RPC takes the first day of the
 * month and raises on anything else. There is deliberately NO catalog read and NO
 * `unit_price` / `price_kind` / `gross` on the org row — those would ship the
 * margin (plan §5). Per-key labels are i18n on `usage_key` / `unit`, never a
 * DB name.
 *
 * The wire interfaces are shared by the API handlers (response construction)
 * and the web hooks (unwrap<T> targets) so the two sides cannot drift.
 */
import { z } from "zod";
/** `YYYY-MM` — the only month shape either usage read accepts. */
export const usageMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
/** Query params for both usage summary reads. */
export const usageSummaryQuerySchema = z.object({
    month: usageMonthSchema,
});
/** Query params for the org billing-history read — `to` is the newest month of the window. */
export const usageHistoryQuerySchema = z.object({ to: usageMonthSchema });
/**
 * `usage_catalog.quantity_kind` — what the page switches on to pick a
 * formatter (`money` → CurrencyDisplay, the rest → the matching number
 * formatter). Never switch on a `usage_key`.
 */
export const usageQuantityKindEnum = z.enum(["count", "money", "duration", "bytes"]);
// ---------------------------------------------------------------------------
// Per-type drill-down — the expanded-row detail reads
// ---------------------------------------------------------------------------
//
//   GET /api/v1/usage/orgs/:orgId/types/:usageKey/detail?month=YYYY-MM
//       → `{ events, snapshots, truncated }` (UsageTypeDetailResponse)
//   GET /api/v1/usage/platform/orgs/:orgKey/types/:usageKey/detail?month=YYYY-MM
//       → the same plus `unit_price` / `price_kind` / `unattributed_reason` /
//         `request_id` on events, `unattributed_reason` on snapshots, and the
//         envelope-level `timezone` (PlatformUsageTypeDetailResponse)
//
// Two bounded reads on the caller's RLS client — no RPC, no view, no keyset
// (docs/USAGE_TRACKING.md "Drill-down": the bounded tier by design). The
// client cannot tell an event type from a stock type (the org summary carries
// no `kind` and the catalog is super-admin-only), so one route returns both
// shapes and one of the two arrays is empty.
/** `usage_catalog.key` shape — the `:usageKey` path param of the detail reads. */
export const usageKeyParamSchema = z.string().regex(/^[a-z0-9_]+$/).max(64);
/**
 * `:orgKey` of the platform detail read — an org id, or the literal
 * `unattributed` for the NULL-org bucket. Validated so a bad value is a 400,
 * never a Postgres uuid-cast message.
 */
export const usageOrgKeyParamSchema = z.union([z.string().uuid(), z.literal("unattributed")]);
/**
 * Both detail queries ask for `USAGE_DETAIL_LIMIT + 1` rows, drop the extra
 * and set `truncated` — a cap, never a promise of completeness. Snapshots need
 * it too: the NULL-org bucket holds one row per reason per day.
 */
export const USAGE_DETAIL_LIMIT = 200;
export const usageEmissionBindingsQuerySchema = z
    .object({
    asOf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
})
    .strict();
//# sourceMappingURL=usageReads.js.map